import { useDataTableContext } from '../context';
import { DataTableColumnMeta } from '../types';
import { validateColumnFilterType } from '../utils';
import { MultiSelectFilter } from './multi-select-filter';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface ColumnMultiSelectFilterProps {
  columnId: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Column-bound multi-select filter that syncs with DataTable's filter state.
 * Automatically validates column type compatibility and manages filter value.
 * @param {ColumnMultiSelectFilterProps} props - Component props
 * @param {string} props.columnId - ID of the column to filter
 * @param {string} props.label - Display label for the filter
 * @param {Array} props.options - Available options with value/label pairs
 * @param {string} [props.placeholder] - Placeholder text (default: 'Select...')
 * @throws {Error} If column type is not 'enum' or 'boolean'
 * @return {JSX.Element} The multi-select filter element
 */
export function ColumnMultiSelectFilter({
  columnId,
  label,
  options,
  placeholder = 'Select...',
}: ColumnMultiSelectFilterProps) {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);
  const meta = column?.columnDef.meta as DataTableColumnMeta | undefined;

  validateColumnFilterType('multiSelect', meta?.type, columnId);

  const value = (column?.getFilterValue() as string[]) ?? [];

  const handleChange = (newValue: string[]) => {
    column?.setFilterValue(newValue.length > 0 ? newValue : undefined);
  };

  return (
    <MultiSelectFilter
      id={`${columnId}-filter`}
      label={label}
      value={value}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
    />
  );
}
